import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditRootCauseTrend } from '../audit.models';
import { AuditService } from '../audit.service';
import { auditNavItems } from '../audit-ui';
import { PermissionService } from '../../security/permission.service';

@Component({
  selector: 'app-audit-root-cause-trends-report',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  template: `
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Audit Reports" title="Root-Cause Trends" subtitle="Trend corrective and preventive actions by root cause, open workload, and overdue exposure.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="item.route !== '/audit/reports/root-cause-trends'" severity="secondary"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/actions" label="Actions" icon="pi pi-check-square" severity="secondary" [outlined]="true"></a>
          <p-button label="Refresh" icon="pi pi-refresh" (onClick)="load()" [outlined]="true" severity="secondary"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Trend Rows" subtitle="Grouped by corrective-action root cause." [resultText]="rows.length + ' root causes'">
        <p-table [value]="rows" styleClass="p-datatable-sm" responsiveLayout="scroll">
          <ng-template pTemplate="header">
            <tr>
              <th>Root Cause</th>
              <th>Total Actions</th>
              <th>Open Actions</th>
              <th>Overdue Actions</th>
              <th>Risk</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><strong>{{ row.rootCause || 'Unspecified' }}</strong></td>
              <td>{{ row.actionCount || 0 }}</td>
              <td>{{ row.openActionCount || 0 }}</td>
              <td>{{ row.overdueActionCount || 0 }}</td>
              <td>
                <p-tag [value]="riskLabel(row)" [severity]="(row.overdueActionCount || 0) > 0 ? 'danger' : (row.openActionCount || 0) > 0 ? 'warn' : 'success'"></p-tag>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="5" class="text-center p-4">No root-cause trend data is available.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
  `
})
export class AuditRootCauseTrendsReportComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly permissionService = inject(PermissionService);

  rows: AuditRootCauseTrend[] = [];
  loading = false;

  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));

  get metrics(): PageMetric[] {
    return [
      { label: 'Root Causes', value: this.rows.length, tone: 'info' },
      { label: 'Actions', value: this.rows.reduce((sum, row) => sum + (row.actionCount || 0), 0), tone: 'info' },
      { label: 'Open', value: this.rows.reduce((sum, row) => sum + (row.openActionCount || 0), 0), tone: 'warn' },
      { label: 'Overdue', value: this.rows.reduce((sum, row) => sum + (row.overdueActionCount || 0), 0), tone: 'danger' }
    ];
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.auditService.rootCauseTrends().subscribe({
      next: response => {
        this.rows = response.data ?? [];
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.loading = false;
      }
    });
  }

  riskLabel(row: AuditRootCauseTrend): string {
    if ((row.overdueActionCount || 0) > 0) {
      return 'Overdue';
    }
    if ((row.openActionCount || 0) > 0) {
      return 'Open';
    }
    return 'Stable';
  }
}
